import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { TableView } from "../components/table/TableView";
import { Avatar } from "../components/ui/Avatar";
import { Button } from "../components/ui/Button";
import { SEAT_COUNT } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useComments } from "../hooks/useComments";
import { useParty } from "../hooks/useParty";
import { useVote } from "../hooks/useVote";
import { ApiError, apiClient } from "../lib/api";
import type { Celebrity, Comment, User } from "../lib/types";

const MAX_DEPTH = 2;

interface CommentNode extends Comment {
  replies: CommentNode[];
}

function buildCommentTree(flat: Comment[]): CommentNode[] {
  const map = new Map<number, CommentNode>();
  for (const c of flat) map.set(c.id, { ...c, replies: [] });
  const roots: CommentNode[] = [];
  for (const c of flat) {
    const node = map.get(c.id)!;
    if (c.parent_id !== null && map.has(c.parent_id)) {
      map.get(c.parent_id)?.replies.unshift(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

interface CommentThreadProps {
  comment: CommentNode;
  depth: number;
  user: User | null;
  confirmingDeleteId: number | null;
  setConfirmingDeleteId: (id: number | null) => void;
  onDelete: (id: number) => Promise<void>;
  onEdit: (id: number, body: string) => Promise<void>;
  onReply: (body: string, parentId: number) => Promise<void>;
}

function CommentThread({
  comment,
  depth,
  user,
  confirmingDeleteId,
  setConfirmingDeleteId,
  onDelete,
  onEdit,
  onReply,
}: CommentThreadProps) {
  const { t } = useTranslation();
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  async function handleReplySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!replyText.trim()) return;
    setIsSubmittingReply(true);
    try {
      await onReply(replyText.trim(), comment.id);
      setReplyText("");
      setReplyOpen(false);
    } finally {
      setIsSubmittingReply(false);
    }
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editText.trim()) return;
    setIsSubmittingEdit(true);
    try {
      await onEdit(comment.id, editText.trim());
      setEditOpen(false);
    } finally {
      setIsSubmittingEdit(false);
    }
  }

  const canReply = user !== null && depth < MAX_DEPTH;

  return (
    <li className="flex items-start gap-3">
      <Link
        to={`/user/${comment.user.id}`}
        className="flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <Avatar
          name={comment.user.name}
          imageUrl={comment.user.avatar_url}
          size={depth === 0 ? 32 : 24}
        />
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          to={`/user/${comment.user.id}`}
          className="text-sm font-semibold hover:text-amber-500 transition-colors"
        >
          {comment.user.name}
        </Link>
        <p className="text-xs text-stone-400">
          {new Date(comment.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        {editOpen ? (
          <form
            onSubmit={handleEditSubmit}
            className="mt-1.5 flex flex-col gap-2"
          >
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              maxLength={500}
              rows={2}
              // biome-ignore lint/a11y/noAutofocus: editing an existing comment, autofocus is expected
              autoFocus
              className="w-full bg-stone-100 dark:bg-stone-800 rounded-lg px-3 py-2 text-sm resize-none placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={!editText.trim() || isSubmittingEdit}
              >
                {isSubmittingEdit ? t("comments.saving") : t("comments.save")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditOpen(false)}
              >
                {t("comments.cancel")}
              </Button>
              <span className="ml-auto text-xs text-stone-400 dark:text-stone-600">
                {t("comments.characterCount", {
                  current: editText.length,
                  max: 500,
                })}
              </span>
            </div>
          </form>
        ) : (
          <p className="text-sm text-stone-700 dark:text-stone-300 mt-0.5 break-words">
            {comment.body}
          </p>
        )}

        <div className="flex items-center gap-3 mt-1.5">
          {canReply && !editOpen && (
            <button
              type="button"
              onClick={() => {
                setReplyOpen((o) => !o);
                setConfirmingDeleteId(null);
              }}
              className="text-xs text-stone-400 hover:text-amber-500 transition-colors cursor-pointer"
            >
              {t("comments.reply")}
            </button>
          )}
          {user?.id === comment.user.id &&
            !editOpen &&
            (confirmingDeleteId === comment.id ? (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    await onDelete(comment.id);
                    setConfirmingDeleteId(null);
                  }}
                  className="text-xs text-red-500 dark:text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                >
                  {t("confirmation.confirm")}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDeleteId(null)}
                  className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors cursor-pointer"
                >
                  {t("confirmation.cancel")}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditText(comment.body);
                    setEditOpen(true);
                    setReplyOpen(false);
                    setConfirmingDeleteId(null);
                  }}
                  className="text-xs text-stone-400 hover:text-amber-500 transition-colors cursor-pointer"
                >
                  {t("comments.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingDeleteId(comment.id);
                    setReplyOpen(false);
                  }}
                  className="text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors cursor-pointer"
                >
                  {t("comments.delete")}
                </button>
              </>
            ))}
        </div>

        {replyOpen && (
          <form
            onSubmit={handleReplySubmit}
            className="mt-2 flex flex-col gap-2"
          >
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t("comments.replyPlaceholder", {
                name: comment.user.name,
              })}
              maxLength={500}
              rows={2}
              className="w-full bg-stone-100 dark:bg-stone-800 rounded-lg px-3 py-2 text-sm resize-none placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={!replyText.trim() || isSubmittingReply}
              >
                {isSubmittingReply ? t("comments.posting") : t("comments.post")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setReplyOpen(false);
                  setReplyText("");
                }}
              >
                {t("comments.cancel")}
              </Button>
              <span className="ml-auto text-xs text-stone-400 dark:text-stone-600">
                {t("comments.characterCount", {
                  current: replyText.length,
                  max: 500,
                })}
              </span>
            </div>
          </form>
        )}

        {comment.replies.length > 0 && (
          <ul className="mt-3 flex flex-col gap-3 pl-2 border-l-2 border-stone-100 dark:border-stone-800">
            {comment.replies.map((reply) => (
              <CommentThread
                key={reply.id}
                comment={reply}
                depth={depth + 1}
                user={user}
                confirmingDeleteId={confirmingDeleteId}
                setConfirmingDeleteId={setConfirmingDeleteId}
                onDelete={onDelete}
                onEdit={onEdit}
                onReply={onReply}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

export function Party() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { user, refetch: refetchUser } = useAuth();
  const navigate = useNavigate();
  const { party, isLoading, error } = useParty(slug);
  const isOwner = Boolean(user && party && user.id === party.user_id);
  const { castVote, removeVote, isSubmitting } = useVote();
  const {
    comments,
    isLoading: commentsLoading,
    isSubmitting: commentSubmitting,
    addComment,
    editComment,
    deleteComment,
  } = useComments(slug ?? "");

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [unpublishError, setUnpublishError] = useState<string | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteCount, setVoteCount] = useState<number | null>(null);
  const [creditsLeft, setCreditsLeft] = useState<number | null>(null);
  const [commentText, setCommentText] = useState("");
  const [votePopKey, setVotePopKey] = useState(0);
  const [particles, setParticles] = useState<{ id: number; tx: string }[]>([]);
  const [voters, setVoters] = useState<
    { id: number; name: string; avatar_url: string | null }[]
  >([]);
  const commentsOpen = Boolean(useMatch("/party/:slug/comments"));
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(
    null,
  );

  function openComments() {
    navigate(`/party/${slug}/comments`, { replace: true });
  }
  function closeComments() {
    navigate(`/party/${slug}`, { replace: true });
  }

  useEffect(() => {
    if (party?.has_voted !== undefined) setHasVoted(party.has_voted);
  }, [party?.has_voted]);

  useEffect(() => {
    if (party?.voters) setVoters(party.voters);
  }, [party]);

  async function handleCopyLink() {
    await navigator.clipboard.writeText(window.location.href);
    toast.success(t("share.copied"));
  }

  function getShareXUrl() {
    const text = t("share.tweetText", { title: party?.title });
    return `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
  }
  const displayVoteCount = voteCount ?? party?.vote_count ?? 0;

  if (isLoading) {
    return (
      <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 flex items-center justify-center pt-14">
        <div className="w-5 h-5 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
      </main>
    );
  }

  if (error || !party) {
    return (
      <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 flex items-center justify-center pt-14">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">
            {t("errors.partyNotFound")}
          </h1>
          <Link to="/" className="text-amber-500 hover:text-amber-400 text-sm">
            {t("nav.home")}
          </Link>
        </div>
      </main>
    );
  }

  // Build seats array (6 slots) from guests
  const seats: (Celebrity | null)[] = Array(SEAT_COUNT).fill(null);
  for (const guest of party.guests) {
    if (guest.seat_order >= 0 && guest.seat_order < SEAT_COUNT) {
      seats[guest.seat_order] = guest.celebrity;
    }
  }

  async function handleUnpublish() {
    if (!party) return;
    setIsUnpublishing(true);
    setUnpublishError(null);
    try {
      await apiClient.patch(`/api/parties/${slug}`, { status: "draft" });
      window.location.reload();
    } catch (err) {
      setUnpublishError(
        err instanceof ApiError ? err.message : t("errors.partyNotFound"),
      );
      setIsUnpublishing(false);
    }
  }

  async function handleDelete() {
    if (!party) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/parties/${slug}`);
      navigate("/");
    } catch {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleVote() {
    if (!party || !user) return;
    try {
      if (hasVoted) {
        const count = await removeVote(party.id);
        setVoteCount(count);
        setHasVoted(false);
        setCreditsLeft((c) => (c ?? user.vote_credits) + 1);
        setVoters((v) => v.filter((v) => v.id !== user.id));
      } else {
        const count = await castVote(party.id);
        setVoteCount(count);
        setHasVoted(true);
        setCreditsLeft((c) => (c ?? user.vote_credits) - 1);
        setVoters((v) => [
          ...v,
          { id: user.id, name: user.name, avatar_url: user.avatar_url },
        ]);
        const burst = Array.from({ length: 8 }, (_, i) => ({
          id: Date.now() + i,
          tx: `${Math.round((Math.random() - 0.5) * 80)}px`,
        }));
        setParticles(burst);
        setTimeout(() => setParticles([]), 750);
      }
      setVotePopKey((k) => k + 1);
      refetchUser();
    } catch (err) {
      // If server says already voted, sync state accordingly
      if (err instanceof ApiError && err.status === 409) setHasVoted(true);
    }
  }

  return (
    <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14">
      <div className="max-w-2xl mx-auto px-4 py-12 flex flex-col gap-8">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors text-sm"
            >
              {t("nav.back")}
            </Link>
            <div className="flex items-center gap-3">
              {party.status === "published" && (
                <button
                  type="button"
                  aria-label="Comments"
                  onClick={openComments}
                  className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors cursor-pointer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  {comments.length > 0 && <span>{comments.length}</span>}
                </button>
              )}
              {/* Share on X */}
              <a
                href={getShareXUrl()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("share.shareOnX")}
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="sr-only">{t("share.shareOnX")}</span>
              </a>
              {/* Copy link */}
              <button
                type="button"
                onClick={handleCopyLink}
                aria-label={t("share.copyLink")}
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
            </div>
          </div>

          {party.status === "draft" && (
            <div className="bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 text-sm px-4 py-2 rounded-lg text-center mb-4">
              {t("party.draftNotice")}
            </div>
          )}

          <h1 className="text-5xl font-bold text-stone-900 dark:text-stone-100 mb-3">
            {party.title}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            {party.user && (
              <Link to={`/user/${party.user.id}`}>
                <Avatar
                  name={party.user.name}
                  imageUrl={party.user.avatar_url}
                  size={20}
                />
              </Link>
            )}
            <p className="text-stone-500 text-sm">
              {party.user ? (
                <>
                  {t("party.byPrefix")}{" "}
                  <Link
                    to={`/user/${party.user.id}`}
                    className="hover:text-amber-500 transition-colors"
                  >
                    {party.user.name}
                  </Link>
                  {" · "}
                </>
              ) : (
                ""
              )}
              {new Date(party.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {/* Owner management row */}
          {isOwner && (
            <div className="flex items-center gap-4 mt-2 pt-3 border-t border-stone-100 dark:border-stone-800">
              <button
                type="button"
                onClick={() => navigate(`/build/${slug}`)}
                className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors cursor-pointer"
              >
                {t("party.edit")}
              </button>
              {party.status === "published" && (
                <button
                  type="button"
                  onClick={handleUnpublish}
                  disabled={isUnpublishing}
                  className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isUnpublishing
                    ? t("party.unpublishing")
                    : t("party.unpublish")}
                </button>
              )}
              {unpublishError && (
                <span className="text-red-500 dark:text-red-400 text-xs">
                  {unpublishError}
                </span>
              )}
              {confirmDelete ? (
                <span className="flex items-center gap-3">
                  <span className="text-xs text-stone-400">
                    {t("confirmation.areYouSure")}
                  </span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="text-xs text-red-500 dark:text-red-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {isDeleting
                      ? t("party.deleting")
                      : t("confirmation.confirm")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors cursor-pointer"
                  >
                    {t("confirmation.cancel")}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors cursor-pointer"
                >
                  {t("party.delete")}
                </button>
              )}
            </div>
          )}
        </div>

        <TableView seats={seats} readonly={true} displayMode={true} />

        {/* Vote section — hidden for drafts */}
        {party.status === "published" && (
          <div className="bg-white dark:bg-stone-900 rounded-2xl p-8 flex flex-col items-center gap-4 border border-stone-200 dark:border-transparent">
            <div className="text-center relative">
              {/* Particle burst */}
              {particles.map((p) => (
                <span
                  key={p.id}
                  className="animate-particle pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 text-amber-400 text-lg select-none"
                  style={{ "--tx": p.tx } as React.CSSProperties}
                >
                  ✦
                </span>
              ))}
              <div
                key={votePopKey}
                className={`text-6xl font-bold animate-vote-pop ${
                  hasVoted
                    ? "text-amber-500"
                    : "text-stone-900 dark:text-stone-100"
                }`}
              >
                {displayVoteCount}
              </div>
              <div className="text-stone-500 dark:text-stone-400 text-xl mt-1">
                {t("votes.label")}
              </div>
            </div>

            {voters.length > 0 && (
              <div className="flex flex-wrap justify-center gap-y-1 -space-x-2">
                {voters.map((voter) => (
                  <div
                    key={voter.id}
                    title={voter.name}
                    className="ring-2 ring-white dark:ring-stone-900 rounded-full"
                  >
                    <Avatar
                      name={voter.name}
                      imageUrl={voter.avatar_url}
                      size={28}
                    />
                  </div>
                ))}
              </div>
            )}

            {!user ? (
              <a
                href={`/auth/login?returnTo=${encodeURIComponent(`/party/${slug}`)}`}
                className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
              >
                {t("auth.signInToVote")}
              </a>
            ) : isOwner ? (
              <p className="text-stone-400 dark:text-stone-600 text-sm">
                {t("party.ownPartyRestriction")}
              </p>
            ) : isSubmitting ? (
              <div className="text-stone-500 dark:text-stone-400 text-sm">
                {t("votes.updating")}
              </div>
            ) : !hasVoted && (creditsLeft ?? user.vote_credits) <= 0 ? (
              <div className="flex flex-col items-center gap-1">
                <p className="text-stone-400 dark:text-stone-600 text-sm">
                  {t("votes.noRemaining")}
                </p>
                <Link
                  to="/build"
                  className="text-amber-500 hover:text-amber-400 text-xs transition-colors"
                >
                  {t("votes.earnCredits")}
                </Link>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Button
                  variant={hasVoted ? "ghost" : "primary"}
                  onClick={handleVote}
                  disabled={isSubmitting}
                >
                  {hasVoted ? t("votes.remove") : t("votes.cast")}
                </Button>
                {!hasVoted && (
                  <div className="flex flex-col items-center gap-0.5">
                    <p className="text-stone-400 dark:text-stone-600 text-xs">
                      {t("votes.remaining", {
                        count: creditsLeft ?? user.vote_credits,
                      })}
                    </p>
                    {(creditsLeft ?? user.vote_credits) <= 1 && (
                      <Link
                        to="/build"
                        className="text-amber-500 hover:text-amber-400 text-xs transition-colors"
                      >
                        {t("votes.earnCredits")}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comments drawer */}
      {party.status === "published" && (
        <>
          {/* Backdrop */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: backdrop dismiss is supplementary — drawer has a close button */}
          <div
            className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${commentsOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={closeComments}
          />
          {/* Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white dark:bg-stone-900 z-50 flex flex-col shadow-2xl transition-transform duration-300 ${commentsOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 dark:border-stone-800">
              <h2 className="font-semibold">
                {t("comments.heading")}{" "}
                {comments.length > 0 && (
                  <span className="text-stone-400 font-normal text-sm">
                    ({comments.length})
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={closeComments}
                className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
              {user && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!commentText.trim()) return;
                    await addComment(commentText.trim());
                    setCommentText("");
                  }}
                  className="flex flex-col gap-2"
                >
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t("comments.addPlaceholder")}
                    maxLength={500}
                    rows={3}
                    className="w-full bg-stone-100 dark:bg-stone-800 rounded-lg px-3 py-2 text-sm resize-none placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-stone-400">
                      {t("comments.characterCount", {
                        current: commentText.length,
                        max: 500,
                      })}
                    </span>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!commentText.trim() || commentSubmitting}
                    >
                      {commentSubmitting
                        ? t("comments.posting")
                        : t("comments.post")}
                    </Button>
                  </div>
                </form>
              )}

              {commentsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-4 h-4 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-stone-400 dark:text-stone-600 text-sm">
                  {user ? (
                    t("comments.beFirst")
                  ) : (
                    <>
                      <a
                        href={`/auth/login?returnTo=${encodeURIComponent(`/party/${slug}/comments`)}`}
                        className="text-amber-500 hover:text-amber-400"
                      >
                        {t("auth.signInToComment")}
                      </a>{" "}
                      {t("auth.toLeaveAComment")}
                    </>
                  )}
                </p>
              ) : (
                <ul className="flex flex-col gap-4">
                  {buildCommentTree(comments).map((comment) => (
                    <CommentThread
                      key={comment.id}
                      comment={comment}
                      depth={0}
                      user={user}
                      confirmingDeleteId={confirmingDeleteId}
                      setConfirmingDeleteId={setConfirmingDeleteId}
                      onDelete={deleteComment}
                      onEdit={editComment}
                      onReply={addComment}
                    />
                  ))}
                </ul>
              )}

              {!user && comments.length > 0 && (
                <p className="text-stone-400 dark:text-stone-600 text-sm">
                  <a
                    href={`/auth/login?returnTo=${encodeURIComponent(`/party/${slug}/comments`)}`}
                    className="text-amber-500 hover:text-amber-400"
                  >
                    {t("auth.signInToComment")}
                  </a>{" "}
                  {t("auth.toLeaveAComment")}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );
}
